import { useEffect, useRef, useState } from "react";
import useTbdCheck from "./useTbdCheck";
import { useDispatch, useSelector } from "react-redux";
import { fetchZones } from "../redux/slices/zoneCountSlice";
import { refreshTbdData } from "../redux/slices/tbdApiSlice";
import { selectUserDetails, selectUserRoles } from "../redux/slices/userSlice";
import { setJobDetail } from "../redux/slices/jobDetailsSlice";
import { postChangeLog } from "../redux/slices/postChangeLogSlice";
import { selectJobId, selectGeneralProject } from "../redux/selectors";
import { patchData } from "../redux/slices/apiCallSlice";
import { selectUpdatedApis } from "../redux/slices/apiUrlSlice";
import { postChangeOrderDetail } from "../redux/slices/postChangeOrderDetailSlice";
import axiosInstance from "../axios/axiosConfig";
import { formActivity } from '../realtime/formActivity';
import { normalizeToggleValue } from '../utils/toggleValues';

const useHandleBlur = (formData, zone = null, { onSaved } = {}) => {
  const [prevValues, setPrevValues] = useState({});
  const [highlightedFields, setHighlightedFields] = useState({});
  const [saveStatus, setSaveStatus] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const dispatch = useDispatch();
  const storedJobDetails = useSelector((state) => state.jobDetails.jobDetails);
  const jobId = useSelector(selectJobId);
  const generalProject = useSelector(selectGeneralProject);
  const updatedApis = useSelector(selectUpdatedApis);

  const { tbdFields } = useTbdCheck(formData, zone);

  const notificationTimeout = useRef(null);
  useEffect(() => () => clearTimeout(notificationTimeout.current), []);
  const lastUpdateTimestamp = {};

  const waterTreatmentBooleanFields = new Set([
    "HasFilters",
    "HasInletFilter",
    "HasInletStrainer",
    "HasDischargeFilter",
    "HasWyeStrainer",
    "HasRO",
    "HasUV",
  ]);

  const shouldSendBooleanValue = (fieldName, sectionKey) => {
    const booleanFields = [
      "SendDocumentsToSales",
      "CustomControlsEngineering",
      "NewConstruction",
      "HasSubmittal",
      "HasSkid",
      "HasPiping",
      "HasEnclosure",
      "HasDropPipes",
      "HasMain",
      "HasLevelControls",
      "HasSky",
      "HasShipLoose",
      "HasShipEarly",
      "HasUnregulated",
      "HasRegulated",
      "HasTelemetry",
      "HasControlLogic",
    ];

    return (
      booleanFields.includes(fieldName) ||
      (sectionKey === "waterTreatment" &&
        waterTreatmentBooleanFields.has(fieldName))
    );
  };

  const handleFocus = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    setPrevValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlur = async (e, label, options = [], updateData = {}) => {
    const { ID, url, field, tableKey, timestamp = new Date().getTime() } = updateData;

    if (!ID || !url || !field || !tableKey) {
      console.error("Error: ID, URL, tableKey, or field is missing in handleBlur.", { ID, url, field, tableKey });
      return;
    }

    let savedNotificationSent = false;
    const name = e.target.name;
    let newValue = e.target.value;
    let formattedNewValue = newValue;
    let formattedPrevValue = prevValues[name];
    
    const currentTimestamp = timestamp || new Date().getTime();
    
    if (!lastUpdateTimestamp[name] || currentTimestamp > lastUpdateTimestamp[name]) {
      lastUpdateTimestamp[name] = currentTimestamp;
    } else {
      return;
    }

    // Handle boolean values for toggles
    if (e.target.type === "switch" || e.target.type === "checkbox") {
      formattedNewValue = normalizeToggleValue(newValue) ? "Yes" : "No";
      formattedPrevValue = normalizeToggleValue(prevValues[name]) ? "Yes" : "No";
      
      if (shouldSendBooleanValue(field, tableKey)) {
        // Send actual boolean values for these specific fields
        newValue = normalizeToggleValue(newValue);
      } else if (field === "HasFertigation") {
        // Keep HasFertigation as 1/0 for backwards compatibility
        newValue = normalizeToggleValue(newValue) ? 1 : 0;
      } else {
        // Convert to string "true" or "false" for other boolean fields
        newValue = normalizeToggleValue(newValue) ? "true" : "false";
      }
    }

    // Handle specific boolean fields that come from dropdown selects with "true"/"false" string values
    const dropdownBooleanFields = ["SendDocumentsToSales", "CustomControlsEngineering", "NewConstruction", "HasSubmittal"];
    if (dropdownBooleanFields.includes(field) && e.target.type === "select-one") {
      // Convert "true"/"false" strings to actual booleans for API
      if (newValue === "true") {
        newValue = true;
        formattedNewValue = "Yes";
      } else if (newValue === "false") {
        newValue = false;
        formattedNewValue = "No";
      }
      
      // Format previous value for display
      if (prevValues[name] === "true" || prevValues[name] === true) {
        formattedPrevValue = "Yes";
      } else if (prevValues[name] === "false" || prevValues[name] === false) {
        formattedPrevValue = "No";
      }
      
    }

    const prevValue = prevValues[name];
    const currentUser = userDetails?.name || "Unknown User";
    const currentRole = userRoles[0] || "Unknown Role";

    const changeLogData = {
      projectID: jobId,
      currentUser,
      userRole: currentRole,
      section: tableKey,
      field: label,
      prevValue: formattedPrevValue,
      newValue: formattedNewValue,
      dateTime: new Date().toISOString(),
    };

    // ChangeOrderDetails requires specific keys (title/existingValue/newValue)
    // and does not use the same shape as the change-log endpoint.
    const changeOrderDetailData = {
      projectID: jobId,
      title: `${label || field} (${tableKey})`,
      existingValue:
        formattedPrevValue === undefined || formattedPrevValue === null || formattedPrevValue === ""
          ? "BLANK"
          : String(formattedPrevValue),
      newValue:
        formattedNewValue === undefined || formattedNewValue === null || formattedNewValue === ""
          ? "BLANK"
          : String(formattedNewValue),
      status: "Pending",
      // Keep these for backward compatibility with any legacy backend mapping.
      section: tableKey,
      field: label || field,
      prevValue: formattedPrevValue,
      dateTime: new Date().toISOString(),
      currentUser,
      userRole: currentRole,
    };

    // Only proceed if value has changed
    if (prevValue !== newValue) {
      // Highlight the field as changed
      setHighlightedFields((prev) => ({
        ...prev,
        [name]: true,
      }));

      // Reset notification state
      if (notificationTimeout.current) {
        clearTimeout(notificationTimeout.current);
      }
      const isToggle = e.target.type === "switch" || e.target.type === "checkbox";
      if (isToggle) setSaveStatus("Saving...");
      setShowNotification(isToggle);

      try {
        // Create payload for patch request
        const patchPayload = { 
          ID: ID,
          [field]: newValue
        };
        
        // Determine the correct URL based on whether the API has been updated
        // Check if this API is in the updatedApis list
        const isUpdated = updatedApis[tableKey];
        let patchUrl = url;
        
        if (isUpdated) {
          // For updated APIs, add the appropriate query parameter
          if (tableKey === "generalProject") {
            patchUrl = `${url}?id=${ID}`;
          } else {
            patchUrl = `${url}?projectID=${jobId}`;
          }
        }
        
        
        // Try using the direct axiosInstance first for better retry handling
        try {
          // This approach gives us the benefit of automatic retries on 500 errors
          const response = await axiosInstance.patch(patchUrl, patchPayload);
          console.log("Direct PATCH result:", response.data);
          notifySaved();
          
          // If the direct axios call is successful, we don't need to use the Redux dispatch
          if (lastUpdateTimestamp[name] === currentTimestamp) {
            setSaveStatus("Saved successfully!");
            setShowNotification(true);
              dispatch(postChangeLog(changeLogData));
            
            // Email notifications disabled
            // if (field !== "ProjectStatus") {
            //   try {
            //     checkAndNotify({ tableKey, field, newValue });
            //   } catch (notifyError) {
            //     console.error("Error in checkAndNotify:", notifyError);
            //   }
            // }

            // Handle change order detail if needed
            if (userRoles.includes("Admin") || userRoles.includes("Apps")) {
              const currentDate = new Date();
              const releaseDate = generalProject[0]?.ReleaseDate
                ? new Date(generalProject[0].ReleaseDate)
                : null;

              if (releaseDate && releaseDate <= currentDate) {
                dispatch(postChangeOrderDetail(changeOrderDetailData));
              }
            }

            // Update Redux store and other necessary actions
            updateReduxStore();
          }
        } catch (directAxiosError) {
          console.warn("Direct PATCH failed, falling back to Redux thunk:", directAxiosError);
            // Fall back to the Redux thunk approach as a backup
          await dispatch(
            patchData({ 
              url: patchUrl, 
              data: patchPayload 
            })
          ).unwrap();
          notifySaved();

          if (lastUpdateTimestamp[name] === currentTimestamp) {
            setSaveStatus("Saved successfully!");
            setShowNotification(true);
              dispatch(postChangeLog(changeLogData));
            
            // Email notifications disabled
            // try {
            //   checkAndNotify({ tableKey, field, newValue });
            // } catch (notifyError) {
            //   console.error("Error in checkAndNotify:", notifyError);
            // }

            // Handle change order detail if needed
            if (userRoles.includes("Admin") || userRoles.includes("Apps")) {
              const currentDate = new Date();
              const releaseDate = generalProject[0]?.ReleaseDate
                ? new Date(generalProject[0].ReleaseDate)
                : null;

              if (releaseDate && releaseDate <= currentDate) {
                dispatch(postChangeOrderDetail(changeOrderDetailData));
              }
            }

            // Update Redux store and other necessary actions
            updateReduxStore();
          }
        }
      } catch (error) {
        console.error("All PATCH attempts failed:", error);
        
        // Update UI on error
        if (lastUpdateTimestamp[name] === currentTimestamp) {
          setSaveStatus("Failed to save after multiple attempts!");
          setShowNotification(true);
          console.error("Error updating job details after retry attempts:", error);
        }
      }

      // Set timeout to hide notification
      notificationTimeout.current = setTimeout(() => {
        setShowNotification(false);
      }, 10000);
    }

    // Optional post-persistence observer; notification failures cannot retry a saved write.
    function notifySaved() {
      if (savedNotificationSent) return;
      savedNotificationSent = true;
      if (onSaved) Promise.resolve().then(() => onSaved({ projectId: jobId, tableKey, field, url }))
        .catch(() => {});
    }

    // Helper function to update Redux store and perform related actions
    function updateReduxStore() {
      // Convert the API string value to appropriate type for Redux
      // For switches/checkboxes, convert "true"/"false" to boolean
      const reduxValue = e.target.type === "switch" || e.target.type === "checkbox" 
        ? normalizeToggleValue(newValue)
        : newValue;

      // Update Redux store
      if (Array.isArray(storedJobDetails[tableKey])) {
        dispatch(
          setJobDetail({
            key: ID,
            value: {
              [field]: reduxValue,
            },
            section: tableKey,
            zone: updateData.zone ?? zone,
          })
        );
      } else if (
        !Array.isArray(storedJobDetails[tableKey]) &&
        storedJobDetails[tableKey]?.ID === ID
      ) {
        dispatch(
          setJobDetail({
            key: ID,
            value: {
              [field]: reduxValue,
            },
            section: tableKey,
            zone: updateData.zone ?? zone ?? 1,
          })
        );
      }
        console.log(`${label} updated successfully.`);
      // Use refreshTbdData() for write operations - it includes the debounced ping
      dispatch(refreshTbdData(jobId));
    }

    // Update zones if needed
    if (field === "Zones") {
      dispatch(fetchZones(ID));
    }
  };

  const handleShowDeleteModal = (id) => {
    setDeleteItemId(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteItemId(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async (deleteFunction) => {
    if (deleteItemId) {
      await deleteFunction(deleteItemId);
      handleCloseDeleteModal();
    }
  };

  return {
    handleFocus,
    handleBlur: async (...args) => {
      const finish = formActivity.saving();
      try { return await handleBlur(...args); }
      finally { finish(); }
    },
    highlightedFields,
    tbdFields,
    saveStatus,
    showNotification,
    handleShowDeleteModal,
    handleCloseDeleteModal,
    handleConfirmDelete,
    showDeleteModal,
  };
};

export default useHandleBlur;
